import { type ComponentProps, useCallback, useState } from 'react'

import { cn } from '@/lib/cn'

interface FadeImgProps extends ComponentProps<'img'> {
  /* Classes for the framing element (aspect ratio, rounding, size). */
  className?: string
  /* Classes for the photograph itself (e.g. animate-hero-drift). */
  imgClassName?: string
}

/* A photograph that develops in: a botanical wash holds the frame until the
   file arrives, then the image fades up over it — no hard pop-in. */
export function FadeImg({
  className,
  imgClassName,
  alt = '',
  ...rest
}: FadeImgProps) {
  const [loaded, setLoaded] = useState(false)
  /* Callback ref so images served from cache (already complete before the
     load listener attaches) still resolve. */
  const attach = useCallback((node: HTMLImageElement | null) => {
    if (node?.complete) setLoaded(true)
  }, [])
  return (
    <span
      className={cn(
        'relative block overflow-hidden bg-surface-tint-strong',
        className,
      )}
    >
      <img
        ref={attach}
        alt={alt}
        onLoad={() => setLoaded(true)}
        className={cn(
          'size-full object-cover transition-opacity duration-500',
          loaded ? 'opacity-100' : 'opacity-0',
          imgClassName,
        )}
        {...rest}
      />
    </span>
  )
}
